// src/components/quizzes/BonusFractionstoDecimals.js
//
// Fractions-to-decimals bonus round.
// Runs on the shared bonus-round engine (kit/BonusShell.js) — the
// BonusDiamonds look, bombs, bonus stars, clocks, combos, a new question
// every few right answers, and 🏁 End game. Same contract as before:
// BonusRouter renders <ThisGame onBonusComplete questions placeholderImage />.

import React from "react";
import BonusShell, { Frac } from "./kit/BonusShell";

const SPAWN_MS = 450;
const MIN_TARGETS_ONSCREEN = 2;    // at least this many correct options on screen
const TARGET_SPEED_SLOWDOWN = 0.9; // correct values fall a bit slower

/** ======= UTIL ======= */
const randomInt = (lo, hi) => lo + Math.floor(Math.random() * (hi - lo + 1));
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

/** Denominators that give terminating decimals in base 10 (2s and 5s only) */
const DEN_POOL = [2, 4, 5, 8, 10, 16, 20, 25, 40, 50];

/** Format a terminating decimal nicely (trim trailing zeros) */
const fmtDecimal = (x, maxPlaces = 4) => {
  // use toFixed then trim
  const s = x.toFixed(maxPlaces);
  return s.replace(/(\.\d*?[1-9])0+$|\.0+$/,'$1');
};

/** Problem model:
 * { n: numerator, d: denominator, target: number (decimal), targetStr: string }
 */
const makeRandomProblem = () => {
  const d = pick(DEN_POOL);
  const n = randomInt(1, d - 1); // proper fraction
  const target = n / d;
  return { n, d, target, targetStr: fmtDecimal(target) };
};

/** Correctness with tolerance to avoid floating noise when parsing strings */
const EPS = 1e-6;
const isCorrectFor = (p, valueStr) => {
  const v = Number(valueStr);
  return Math.abs(v - p.target) <= EPS;
};

/** Sample values */
const pickCorrectValue = (p) => p.targetStr;

const pickIncorrectValue = (p) => {
  // Build distractors near and far; keep same decimal places as targetStr if possible
  const places = p.targetStr.includes(".") ? p.targetStr.split(".")[1].length : 0;
  const step = Math.pow(10, -(Math.min(4, Math.max(1, places || 2))));
  const nearChoices = [-3,-2,-1,1,2,3].map(k => p.target + k * step).filter(x => x >= 0 && x <= 2); // keep in a friendly range
  const farChoices = [0, 0.1, 0.2, 0.25, 0.3, 0.4, 0.5, 0.6, 0.75, 0.8, 0.9, 1, 1.25, 1.5].filter(x => Math.abs(x - p.target) > 0.02);

  const pool = Math.random() < 0.7 ? nearChoices : farChoices;
  let v = pick(pool.length ? pool : farChoices);
  if (v == null) v = p.target + step; // fallback
  if (Math.abs(v - p.target) <= EPS) v += step; // ensure not equal
  // format with similar places
  const s = fmtDecimal(v, Math.max(2, places || 2));
  if (isCorrectFor(p, s)) {
    // nudge a tad more if formatting snapped back to target
    const s2 = fmtDecimal(v + step, Math.max(2, places || 2));
    return s2;
  }
  return s;
};

// keep mix balanced and ensure some correct are present
const shouldSpawnCorrectBalanced = (p, items) => {
  const correct = items.filter(it => isCorrectFor(p, it.value)).length;
  const wrong = items.length - correct;
  if (correct < MIN_TARGETS_ONSCREEN) return true;
  if (correct < wrong) return true;
  if (wrong < correct) return false;
  return Math.random() < 0.5;
};

const FractionsToDecimalsBonus = (props) => (
  <BonusShell
    {...props}
    title="Fractions to Decimals!"
    emoji="🔁"
    sub="Catch the decimals that equal the fraction."
    intro="Fractions to decimals. Catch the decimals that are equal to the fraction. Each one is worth five points. Watch out for bombs! Press Start when you are ready."
    rules={[
      { icon: "✅", text: "Equal decimal", pts: "+5" },
      { icon: "❌", text: "Wrong decimal", pts: "-1" },
    ]}
    init={() => makeRandomProblem()}
    prompt={(p) => ({
      big: (
        <>
          <Frac n={p.n} d={p.d} /> = ?
        </>
      ),
      small: "Catch the matching decimal!",
      say: `What decimal equals ${p.n} over ${p.d}?`,
    })}
    spawn={(p, onScreen) => (shouldSpawnCorrectBalanced(p, onScreen) ? pickCorrectValue(p) : pickIncorrectValue(p))}
    check={isCorrectFor}
    newEvery={3}
    spawnMs={SPAWN_MS}
    slowGood={TARGET_SPEED_SLOWDOWN}
  />
);

export default FractionsToDecimalsBonus;
